import { appLog } from '~src/utils/log';
import {
  appendDuplicateNumbers,
  fileLinkToStream,
  FileToDownload,
  FolderStructureEntry,
} from '~src/utils/downloader';
import streamSaver from 'streamsaver';
import { createWriter as ZIP } from '../../streamSaver/zip-stream';

/** Use StreamSaver to sequentially stream file data to a zip on disk  */
export const writeFilesToZipStream = async (
  rawFilesToDownload: FileToDownload[],
  structure?: FolderStructureEntry[],
  /** zip prefix */
  name = 'Download'
) => {
  // copy filesToDownload, to avoid altering the fn param
  // also append duplicate numbers to avoid errors
  let filesToDownload = appendDuplicateNumbers([...rawFilesToDownload]);
  // build zip filename
  const zipName = `${name}__${Date.now()}.zip`;
  appLog(`writeFilesToZipStream -> creating zip "${zipName}"...`);

  // create write stream to zip file
  const writeStream = streamSaver.createWriteStream(zipName);

  // setup zip stream
  /** For how to use it, refer to this example https://github.com/jimmywarting/StreamSaver.js/blob/master/examples/saving-multiple-files.html */
  const readableZipStream: any = ZIP({
    /** called first */
    start: (ctrl) => {
      // create directories beforehand if given
      if (structure) {
        structure.forEach(({ path }) => {
          ctrl.enqueue({ name: path, directory: true });
        });
      }
    },
    /** called whenever the zip utility needs requests more data */
    pull: async (ctrl) => {
      const file = filesToDownload[0];
      if (!file) {
        ctrl.close();
        return;
      }

      const name = file.filePath;
      // appLog(`writeFilesToZipStream -> pull -> loading "${name}"...`);
      // load the file into stream
      const stream = await fileLinkToStream(file.location);
      // add to zip
      ctrl.enqueue({ name, stream });
      appLog(`writeFilesToZipStream -> pull -> done "${name}"...`);

      // slice da stack
      filesToDownload = filesToDownload.slice(1);
      // done
      if (!filesToDownload.length) {
        ctrl.close();
      }
    },
  });

  // rest is taken directly from the referenced example
  // more optimized
  if (window.WritableStream && readableZipStream.pipeTo) {
    return readableZipStream.pipeTo(writeStream);
  }

  // less optimized
  const writer = writeStream.getWriter();
  const reader = readableZipStream.getReader();
  const pump = () =>
    reader
      .read()
      .then((res) =>
        res.done ? writer.close() : writer.write(res.value).then(pump)
      );

  return pump();
};
